import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  formatBalance,
  formatDate,
  formatDateTime,
  formatMoney,
  parseMoneyInput,
  t,
} from '@aquabox/shared';
import {
  useAccountsSummary,
  useCashCount,
  useCashSeries,
  useCashSummary,
  useCustomers,
  useDriverFloats,
  useFloatSummary,
  useFinanceMutations,
  useMe,
  useReceipts,
} from '../api/queries';
import { ApiError, api } from '../api/client';
import { useReceivablesAging } from '../api/aging';
import { Icon } from '../components/Icon';
import { BalanceCell } from '../components/BalanceCell';
import { Kpi } from '../components/Kpi';
import { StatementDialog } from '../components/StatementDialog';
import { relativeTime } from '../lib/labels';
import { ReceiptModal } from './ReceiptModal';

const TABS = [
  { value: 'kasa', labelKey: 'cash.tabCash' },
  { value: 'cari', labelKey: 'cash.tabAccounts' },
  { value: 'emanet', labelKey: 'cash.tabFloats' },
  { value: 'makbuz', labelKey: 'cash.tabReceipts' },
] as const;

const EXPENSE_KINDS = [
  'EXPENSE_FUEL',
  'EXPENSE_MEAL',
  'EXPENSE_MAINTENANCE',
  'EXPENSE_OTHER',
] as const;

/**
 * Cari bakiye hücresi tek yerden gelir: `components/BalanceCell.tsx` (müşteri ekstresiyle
 * aynı dil — "₺136,50 B" / "₺25,00 A"; renk yönden türetilir, para hesabı yapılmaz).
 */

/**
 * Ön muhasebe (§3.13) — dört sekme.
 *
 * Bütün rakamlar SUNUCUDAN gelir; panel hiçbir para hesabı yapmaz (kural 4). "Kasa" ile "ciro"
 * ayrı gösterilir: nakit satış şoförün üstündedir ve teslim alınana kadar kasada değildir.
 */
export function Cash() {
  const [tab, setTab] = useState<string>('kasa');
  return (
    <div style={{ padding: '28px 32px 36px', display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {TABS.map((item) => (
          <button
            key={item.value}
            onClick={() => setTab(item.value)}
            aria-pressed={tab === item.value}
            style={{
              borderRadius: 'var(--r-pill)',
              padding: '10px 16px',
              fontWeight: 600,
              fontSize: 13.5,
              background: tab === item.value ? 'var(--primary)' : 'var(--surface)',
              border: `1px solid ${tab === item.value ? 'var(--primary)' : 'var(--border)'}`,
              color: tab === item.value ? '#fff' : 'var(--text-secondary)',
            }}
          >
            {t(item.labelKey)}
          </button>
        ))}
      </div>

      {tab === 'kasa' ? <CashTab /> : null}
      {tab === 'cari' ? <AccountsTab /> : null}
      {tab === 'emanet' ? <FloatsTab /> : null}
      {tab === 'makbuz' ? <ReceiptsTab /> : null}
    </div>
  );
}

function CashTab() {
  const summary = useCashSummary();
  const series = useCashSeries(7);
  const { addExpense } = useFinanceMutations();
  const [open, setOpen] = useState(false);
  const [amount, setAmount] = useState('');
  const [kind, setKind] = useState<string>('EXPENSE_FUEL');
  const [note, setNote] = useState('');
  const [invalid, setInvalid] = useState(false);

  const data = summary.data;
  const expenses = Object.entries(data?.expenses ?? {});
  // Kasa sayımı düzeltmesi: sunucunun işaretli dizesi; yalnız sıfırdan farklıysa gösterilir.
  const adjustment =
    data?.adjustment && !/^-?0\.00$/.test(data.adjustment) ? data.adjustment : null;
  // Transfer (çekim/yatırma): gider değil; yalnız sıfırdan farklıysa ayrı satır.
  const transfer = data?.transfer && !/^-?0\.00$/.test(data.transfer) ? data.transfer : null;

  function save(): void {
    // "125,50" → 12.550 ₺ oluyordu: ondalık ayracı siliniyordu (Faz 3 denetimi K2).
    const clean = parseMoneyInput(amount);
    if (clean === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    addExpense.mutate(
      { amount: clean, kind, note: note || undefined, clientId: crypto.randomUUID() },
      {
        onSuccess: () => {
          setOpen(false);
          setAmount('');
          setNote('');
        },
      },
    );
  }

  return (
    <>
      <p style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>{t('cash.dailyNote')}</p>
      <section
        className="section-grid"
        style={{ '--section-cols': '1fr 1fr 300px' } as React.CSSProperties}
      >
        <div className="card" style={{ padding: 20 }}>
          <Dot color="var(--success)" label={t('cash.inflow')} />
          <Big value={formatMoney(data?.inflow ?? '0')} />
          <Line label={t('cash.handover')} value={formatMoney(data?.handover ?? '0')} />
          <Line
            label={t('cash.customerPayment')}
            value={formatMoney(data?.customerPayment ?? '0')}
          />
          {adjustment ? (
            <Line label={t('cashCount.adjustment')} value={formatMoney(adjustment)} />
          ) : null}
          <hr style={{ border: 'none', borderTop: '1px solid var(--border)', margin: '10px 0' }} />
          <div
            style={{
              fontSize: 11,
              fontWeight: 700,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              color: 'var(--text-secondary)',
            }}
          >
            {t('panelUi.salesBreakdownTitle')}
          </div>
          <Line label={t('trips.collectedCash')} value={formatMoney(data?.sales.cash ?? '0')} />
          <Line label={t('trips.collectedCard')} value={formatMoney(data?.sales.card ?? '0')} />
          <Line label={t('trips.collectedOnline')} value={formatMoney(data?.sales.online ?? '0')} />
          <Line label={t('trips.collectedCredit')} value={formatMoney(data?.sales.credit ?? '0')} />
          <p
            style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8, lineHeight: 1.5 }}
          >
            {t('cash.salesHint')}
          </p>
        </div>

        <div className="card" style={{ padding: 20 }}>
          <Dot color="var(--warning)" label={t('cash.outflow')} />
          <Big value={formatMoney(data?.outflow ?? '0')} />
          {expenses.length === 0 ? (
            <p style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{t('cash.noExpenses')}</p>
          ) : (
            expenses.map(([k, v]) => (
              <Line
                key={k}
                label={t(`cashKind.${k}` as Parameters<typeof t>[0])}
                value={formatMoney(v)}
              />
            ))
          )}
          {invalid ? (
            <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>{t('cash.amountInvalid')}</p>
          ) : null}
          {addExpense.isError ? (
            <p style={{ fontSize: 12.5, color: 'var(--danger)' }}>
              {(addExpense.error as { message?: string } | null)?.message ?? t('error.internal')}
            </p>
          ) : null}
          {open ? (
            <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 10 }}>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {t('cash.expenseAmount')}
                </span>
                <input
                  placeholder={t('cash.expenseAmount')}
                  inputMode="decimal"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  style={{ height: 46, fontVariantNumeric: 'tabular-nums' }}
                />
              </label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {EXPENSE_KINDS.map((k) => (
                  <button
                    key={k}
                    onClick={() => setKind(k)}
                    aria-pressed={kind === k}
                    style={{
                      borderRadius: 'var(--r-pill)',
                      padding: '7px 13px',
                      fontSize: 13,
                      fontWeight: 600,
                      background: kind === k ? 'var(--primary-bg)' : 'var(--surface)',
                      border: `1px solid ${kind === k ? 'var(--primary)' : 'var(--border)'}`,
                    }}
                  >
                    {t(`cashKind.${k}` as Parameters<typeof t>[0])}
                  </button>
                ))}
              </div>
              <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {t('cash.expenseNote')}
                </span>
                <input
                  placeholder={t('cash.expenseNote')}
                  value={note}
                  onChange={(e) => setNote(e.target.value)}
                />
              </label>
              <div style={{ display: 'flex', gap: 8 }}>
                <button className="btn" onClick={() => setOpen(false)}>
                  {t('common.cancel')}
                </button>
                <button className="btn btn-primary" onClick={save} disabled={addExpense.isPending}>
                  {t('common.save')}
                </button>
              </div>
            </div>
          ) : (
            <button
              className="btn"
              onClick={() => setOpen(true)}
              style={{ marginTop: 12, color: 'var(--primary)', borderColor: 'var(--primary)' }}
            >
              {t('cash.addExpense')}
            </button>
          )}
        </div>

        <div
          style={{
            background: 'var(--navy-900)',
            borderRadius: 'var(--r-box)',
            padding: '26px 24px',
            color: '#fff',
          }}
        >
          <div
            style={{
              color: 'var(--primary-light)',
              fontSize: 12,
              fontWeight: 700,
              letterSpacing: '.04em',
            }}
          >
            {t('cash.closing').toLocaleUpperCase('tr-TR')}
          </div>
          <div className="kpi-number-hero" style={{ marginTop: 10 }}>
            {formatMoney(data?.closing ?? '0')}
          </div>
          <div style={{ fontSize: 13, opacity: 0.75, marginTop: 6 }}>
            {transfer
              ? t('cash.closingHintTransfer')
              : adjustment
                ? t('cashCount.closingHintAdjusted')
                : t('cash.closingHint')}
          </div>
        </div>
      </section>

      {transfer ? (
        <div className="card" style={{ padding: '14px 20px' }}>
          <Line label={t('cash.transfer')} value={formatMoney(transfer)} />
        </div>
      ) : null}

      <CashCountCard />
      <CashTransferCard />

      <div className="card" style={{ padding: 20 }}>
        <div className="section-label" style={{ marginBottom: 14 }}>
          {t('cash.last7')}
        </div>
        <Chart points={series.data ?? []} />
      </div>
    </>
  );
}

/**
 * Sayılan tutar kutusu: sıfır GEÇERLİ bir sayımdır (boş çekmece), oysa `parseMoneyInput` sıfırı
 * reddeder (para girişi için doğru, sayım için yanlış). Dönen dize backend'e olduğu gibi gider.
 */
function parseCountedInput(raw: string): string | null {
  const trimmed = raw.trim();
  if (/^0+([.,]0{1,2})?$/.test(trimmed)) return '0';
  return parseMoneyInput(trimmed);
}

function errorText(err: unknown): string {
  return (err as { message?: string } | null)?.message ?? t('error.internal');
}

/**
 * Kasa sayımı kartı. Fark SUNUCUDA hesaplanır; panel yalnız gösterir. `clientId` önizleme anında
 * üretilir: aynı önizlemenin yeniden denenmesi (çift tık, ağ hatası) ikinci satır yazmaz.
 */
function CashCountCard() {
  const me = useMe();
  const { preview, save } = useCashCount();
  const [raw, setRaw] = useState('');
  const [note, setNote] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [noteMissing, setNoteMissing] = useState(false);
  const [reviewed, setReviewed] = useState<{
    /// Önizlemede sunucuya giden girdi: değişirse "Kaydet" pasifleşir
    sent: string;
    expected: string;
    counted: string;
    variance: string;
    isFirstCount: boolean;
    clientId: string;
  } | null>(null);
  const [notice, setNotice] = useState<{ text: string; danger: boolean } | null>(null);
  /// "Bu açılış sayımıdır": OWNER'ın AÇIK seçimi; varsayılan işaretsiz.
  const [opening, setOpening] = useState(false);

  const isOwner = me.data?.role === 'OWNER';
  const stale = reviewed !== null && parseCountedInput(raw) !== reviewed.sent;
  // Fark ≠ 0 kararı sunucunun dizesinden okunur ("0.00" / "-0.00" dışı); hesap yok.
  const hasVariance = reviewed !== null && !/^-?0\.00$/.test(reviewed.variance);

  function runPreview(): void {
    setNotice(null);
    setNoteMissing(false);
    const sent = parseCountedInput(raw);
    if (sent === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    preview.mutate(sent, {
      onSuccess: (res) =>
        setReviewed({
          sent,
          expected: res.expected,
          counted: res.counted,
          variance: res.variance,
          isFirstCount: res.isFirstCount,
          clientId: crypto.randomUUID(),
        }),
      onError: (err) => setNotice({ text: errorText(err), danger: true }),
    });
  }

  function runSave(): void {
    if (!reviewed || stale) return;
    const trimmed = note.trim();
    if (hasVariance && trimmed === '') {
      setNoteMissing(true);
      return;
    }
    setNoteMissing(false);
    save.mutate(
      {
        counted: reviewed.sent,
        expected: reviewed.expected,
        note: trimmed === '' ? undefined : trimmed,
        clientId: reviewed.clientId,
        ...(reviewed.isFirstCount && opening ? { opening: true } : {}),
      },
      {
        onSuccess: (res) => {
          setNotice({
            // Sıfır fark da satır yazar; ayrım sunucunun dizesinden okunur (hesap yok).
            text: /^-?0\.00$/.test(res.variance) ? t('cashCount.savedZero') : t('cashCount.saved'),
            danger: false,
          });
          setReviewed(null);
          setRaw('');
          setNote('');
          setOpening(false);
        },
        onError: (err) => {
          // Defter önizlemeden sonra değişti (409): eski fark yazılmaz, yeniden önizlenir.
          // P2034 (`CASH_COUNT_RETRY`) geçicidir: önizleme ve clientId KALIR, aynı istek yeniden
          // gönderilebilir (sunucuda replay devreye girer). Diğer 409'lar (defter değişti,
          // anahtar uyuşmazlığı) önizlemeyi geçersiz kılar. Ayrım mesaj metnine değil KODA bakar.
          if (err instanceof ApiError && err.status === 409 && err.code !== 'CASH_COUNT_RETRY') {
            setReviewed(null);
            setOpening(false);
          }
          setNotice({ text: errorText(err), danger: true });
        },
      },
    );
  }

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div className="section-label">{t('cashCount.title')}</div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
        {t('cashCount.hint')}
      </p>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('cashCount.counted')}
          </span>
          <input
            inputMode="decimal"
            value={raw}
            onChange={(e) => setRaw(e.target.value)}
            style={{ height: 46, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
        <button className="btn" onClick={runPreview} disabled={preview.isPending}>
          {t('cashCount.preview')}
        </button>
      </div>
      {invalid ? (
        <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0 }}>
          {t('cashCount.amountInvalid')}
        </p>
      ) : null}

      {reviewed ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <Line label={t('cashCount.expected')} value={formatMoney(reviewed.expected)} />
          <Line label={t('cashCount.countedResult')} value={formatMoney(reviewed.counted)} />
          <Line label={t('cashCount.variance')} value={formatMoney(reviewed.variance)} />
          {reviewed.isFirstCount ? (
            <>
              <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: 0 }}>
                {t('cashCount.firstCountWarning')}
              </p>
              {isOwner ? (
                <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                  <input
                    type="checkbox"
                    checked={opening}
                    onChange={(e) => setOpening(e.target.checked)}
                  />
                  {t('cashCount.openingCheckbox')}
                </label>
              ) : null}
            </>
          ) : null}
          {stale ? (
            <p style={{ fontSize: 12.5, color: 'var(--warning)', margin: 0 }}>
              {t('cashCount.reviewAgain')}
            </p>
          ) : null}
          {!hasVariance ? (
            <p style={{ fontSize: 12.5, color: 'var(--text-secondary)', margin: 0 }}>
              {t('cashCount.varianceZero')}
            </p>
          ) : (
            <label style={{ display: 'flex', flexDirection: 'column', gap: 6, marginTop: 6 }}>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {t('cashCount.note')}
              </span>
              <input maxLength={280} value={note} onChange={(e) => setNote(e.target.value)} />
            </label>
          )}
          {noteMissing ? (
            <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0 }}>
              {t('cashCount.noteRequired')}
            </p>
          ) : null}
          {isOwner ? (
            <div style={{ marginTop: 8 }}>
              <button
                className="btn btn-primary"
                onClick={runSave}
                disabled={stale || save.isPending}
              >
                {t('cashCount.save')}
              </button>
            </div>
          ) : null}
        </div>
      ) : null}

      {notice ? (
        <p
          role="status"
          style={{
            fontSize: 12.5,
            margin: 0,
            color: notice.danger ? 'var(--danger)' : 'var(--success)',
          }}
        >
          {notice.text}
        </p>
      ) : null}
    </div>
  );
}

/**
 * Kasadan çekim / bankaya yatırma (YALNIZ patron). Tutar sunucuya olduğu gibi gider; işareti ve
 * kovayı (gider değil transfer) sunucu belirler. `clientId` form açılışında üretilir: çift tık
 * ikinci satır yazmaz; başarıdan sonra yenilenir.
 */
function CashTransferCard() {
  const me = useMe();
  const { addTransfer } = useFinanceMutations();
  const [kind, setKind] = useState<'OWNER_WITHDRAWAL' | 'BANK_DEPOSIT'>('OWNER_WITHDRAWAL');
  const [raw, setRaw] = useState('');
  const [note, setNote] = useState('');
  const [invalid, setInvalid] = useState(false);
  const [saved, setSaved] = useState(false);
  const [clientId, setClientId] = useState(() => crypto.randomUUID());

  if (me.data?.role !== 'OWNER') return null;

  // clientId YALNIZ başarıdan sonra ya da "Yeni işlem olarak gönder" ile yenilenir. Alan
  // değişince yenilenmez: zaman aşımında istek sunucuda yazılmış olabilir; anahtar korunursa
  // yeniden gönderim replay döner, yenilenirse ikinci çekim satırı oluşurdu.
  const mismatch =
    addTransfer.error instanceof ApiError &&
    addTransfer.error.code === 'CASH_TRANSFER_CLIENT_ID_MISMATCH';

  function submit(id: string = clientId): void {
    setSaved(false);
    const amount = parseMoneyInput(raw);
    if (amount === null) {
      setInvalid(true);
      return;
    }
    setInvalid(false);
    addTransfer.mutate(
      { kind, amount, note: note.trim() === '' ? undefined : note.trim(), clientId: id },
      {
        onSuccess: () => {
          setRaw('');
          setNote('');
          setSaved(true);
          setClientId(crypto.randomUUID());
        },
      },
    );
  }

  return (
    <div
      className="card"
      style={{ padding: 20, display: 'flex', flexDirection: 'column', gap: 12 }}
    >
      <div className="section-label">{t('cashTransfer.title')}</div>
      <p style={{ fontSize: 13, color: 'var(--text-secondary)', margin: 0 }}>
        {t('cashTransfer.hint')}
      </p>
      <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        {(['OWNER_WITHDRAWAL', 'BANK_DEPOSIT'] as const).map((k) => (
          <button
            key={k}
            onClick={() => {
              setKind(k);
            }}
            aria-pressed={kind === k}
            style={{
              borderRadius: 'var(--r-pill)',
              padding: '7px 13px',
              fontSize: 13,
              fontWeight: 600,
              background: kind === k ? 'var(--primary-bg)' : 'var(--surface)',
              border: `1px solid ${kind === k ? 'var(--primary)' : 'var(--border)'}`,
            }}
          >
            {t(`cashKind.${k}` as Parameters<typeof t>[0])}
          </button>
        ))}
      </div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('cashTransfer.amount')}
          </span>
          <input
            inputMode="decimal"
            value={raw}
            onChange={(e) => {
              setRaw(e.target.value);
            }}
            style={{ height: 46, fontVariantNumeric: 'tabular-nums' }}
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
            {t('cashTransfer.note')}
          </span>
          <input
            maxLength={280}
            value={note}
            onChange={(e) => {
              setNote(e.target.value);
            }}
          />
        </label>
        <button
          className="btn btn-primary"
          onClick={() => submit()}
          disabled={addTransfer.isPending}
        >
          {t('cashTransfer.save')}
        </button>
      </div>
      {invalid ? (
        <p style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0 }}>
          {t('cash.amountInvalid')}
        </p>
      ) : null}
      {mismatch ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, alignItems: 'flex-start' }}>
          <p role="alert" style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0 }}>
            {t('cashTransfer.mismatch')}
          </p>
          <button
            className="btn"
            disabled={addTransfer.isPending}
            onClick={() => {
              const fresh = crypto.randomUUID();
              setClientId(fresh);
              submit(fresh);
            }}
          >
            {t('cashTransfer.sendAsNew')}
          </button>
        </div>
      ) : null}
      {addTransfer.isError && !mismatch ? (
        <p role="alert" style={{ fontSize: 12.5, color: 'var(--danger)', margin: 0 }}>
          {errorText(addTransfer.error)}
        </p>
      ) : null}
      {saved ? (
        <p role="status" style={{ fontSize: 12.5, color: 'var(--success)', margin: 0 }}>
          {t('cashTransfer.saved')}
        </p>
      ) : null}
    </div>
  );
}

/** Basit ikili çubuk grafiği (§3.13.1). Ölçek en yüksek güne göre; harici kütüphane yok. */
function Chart({ points }: { points: { date: string; inflow: string; outflow: string }[] }) {
  const max = Math.max(1, ...points.flatMap((p) => [Number(p.inflow), Number(p.outflow)]));
  return (
    <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', height: 150 }}>
      {points.map((p) => (
        <div key={p.date} style={{ flex: 1, textAlign: 'center' }}>
          <div style={{ display: 'flex', gap: 3, alignItems: 'flex-end', height: 120 }}>
            <Bar height={(Number(p.inflow) / max) * 120} color="var(--success)" />
            <Bar height={(Number(p.outflow) / max) * 120} color="var(--warning)" />
          </div>
          <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginTop: 6 }}>
            {p.date.slice(8)}.{p.date.slice(5, 7)}
          </div>
        </div>
      ))}
    </div>
  );
}

function Bar({ height, color }: { height: number; color: string }) {
  return (
    <div
      style={{
        flex: 1,
        height: Math.max(2, height),
        background: color,
        borderRadius: '4px 4px 0 0',
      }}
    />
  );
}

/** Cari hesaplar sekmesi tablo grid'i (§3.13.2): Müşteri · Telefon · Bakiye · Zimmet · Son hareket · aksiyonlar. */
const ACCOUNTS_GRID = '1.2fr 150px 130px 130px 140px 200px';

/** Yaşlandırma kova şeridi — sunucudan gelen tutarları gösterir (panel toplamaz). */
const AGING_BANDS_GRID = '120px 120px 120px 120px 1fr';

/** "Aranacaklar" tablosu: Müşteri · Telefon · Bakiye · En eski borç · Son tahsilat · aksiyon. */
const CALL_LIST_GRID = '1.2fr 150px 130px 180px 160px 160px';

function AccountsTab() {
  const summary = useAccountsSummary();
  const customers = useCustomers({ filter: 'debtors', pageSize: '100' });
  const me = useMe();
  const aging = useReceivablesAging();
  const navigate = useNavigate();
  const d = summary.data;
  /** Ekstre uçsunun sunucudaki yetkisi `@Roles('OWNER','MANAGER')`; panel yalnız gizler. */
  const canStatement = me.data?.role === 'OWNER' || me.data?.role === 'MANAGER';
  const [statementFor, setStatementFor] = useState<{ id: string; name: string } | null>(null);
  const [showAllCallList, setShowAllCallList] = useState(false);
  const agingData = aging.data;
  // Sunucu sıralamasını kullanıyoruz (oldestDays azalan, eşitse balance azalan); panel yeniden
  // sıralamaz yoksa bakiye ile gün farkı karışır ve "aranacaklar" yanlış kişiyi gösterir.
  const callList = agingData?.customers ?? [];
  const callListTop20 = showAllCallList ? callList : callList.slice(0, 20);

  return (
    <>
      <section className="kpi-grid">
        <Kpi
          label={t('cash.receivable')}
          value={formatMoney(d?.receivable ?? '0')}
          accent="warning"
          note={t('cash.receivableHint')}
        />
        <Kpi
          label={t('cash.payable')}
          value={formatMoney(d?.payable ?? '0')}
          note={t('cash.payableHint')}
        />
        <Kpi label={t('cash.debtors')} value={d?.debtorCount ?? 0} />
        <Kpi label={t('cash.jugsOnHand')} value={d?.jugsOnHand ?? 0} />
      </section>

      {/* Yaşlandırma kova şeridi — sunucudan dört tutar, panel toplam YAPMAZ (kural 4). */}
      <div className="card" style={{ padding: 18 }}>
        <div
          style={{
            display: 'flex',
            alignItems: 'baseline',
            justifyContent: 'space-between',
            marginBottom: 12,
          }}
        >
          <div style={{ fontWeight: 700, fontSize: 14 }}>{t('aging.title')}</div>
          <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{t('aging.subtitle')}</div>
        </div>
        {aging.isError ? (
          <p style={{ fontSize: 13, color: 'var(--danger)', margin: 0 }}>{t('aging.loadError')}</p>
        ) : null}
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: AGING_BANDS_GRID,
            alignItems: 'center',
            gap: 14,
            padding: '6px 0 2px',
          }}
        >
          <AgingBand
            label={t('aging.bucket.0_30')}
            value={agingData?.totals.b0_30 ?? null}
            danger={false}
          />
          <AgingBand
            label={t('aging.bucket.31_60')}
            value={agingData?.totals.b31_60 ?? null}
            danger={false}
          />
          <AgingBand
            label={t('aging.bucket.61_90')}
            value={agingData?.totals.b61_90 ?? null}
            danger={false}
          />
          <AgingBand
            label={t('aging.bucket.90p')}
            value={agingData?.totals.b90p ?? null}
            // 90+ kovasında tutar varsa uyarı rengi — patron gözünden kaçmasın. Sunucu
            // tutarı `toFixed(2)` STRING döner; `parseFloat` yok (float kuruş eritir, kural 4).
            danger={!!agingData && agingData.totals.b90p !== '0.00'}
          />
          <div style={{ textAlign: 'right', fontSize: 12, color: 'var(--text-secondary)' }}>
            {t('panel.balance')}:{' '}
            <strong style={{ color: 'var(--text-primary)', fontSize: 14 }}>
              {/* Veri yokken ₺0,00 YAZMAK "borç yok" izlenimi verirdi — bilinmeyeni "—" ile göster. */}
              {agingData ? formatMoney(agingData.totals.total) : '—'}
            </strong>
          </div>
        </div>
      </div>

      <div className="card" style={{ overflow: 'auto' }}>
        <div
          className="table-head table-head-stack"
          style={{
            display: 'grid',
            gridTemplateColumns: ACCOUNTS_GRID,
            minWidth: 940,
            padding: '0 22px',
            height: 44,
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{t('cash.colCustomer')}</span>
          <span>{t('panel.phone')}</span>
          <span style={{ textAlign: 'right' }}>{t('panel.balance')}</span>
          <span style={{ textAlign: 'right' }}>{t('panel.deposit')}</span>
          <span>{t('cash.colLastMove')}</span>
          <span />
        </div>
        {(customers.data?.items ?? []).map((c) => {
          return (
            <div
              key={c.id}
              className="rowgrid-stack"
              style={{
                display: 'grid',
                gridTemplateColumns: ACCOUNTS_GRID,
                minWidth: 940,
                padding: '0 22px',
                height: 58,
                alignItems: 'center',
                gap: 12,
                borderTop: '1px solid var(--border)',
              }}
            >
              <span style={{ fontWeight: 500 }}>{c.fullName}</span>
              <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
                {c.phone ?? '—'}
              </span>
              <BalanceCell balance={c.balance} />
              <span style={{ textAlign: 'right' }}>{c.jugsOnHand}</span>
              <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                {c.lastOrderAt ? relativeTime(c.lastOrderAt) : t('panel.never')}
              </span>
              <span style={{ display: 'flex', gap: 8 }}>
                <button
                  className="btn btn-primary"
                  style={{ fontSize: 12.5, padding: '7px 12px' }}
                  onClick={() => void navigate(`/musteriler?customer=${c.id}&collect=1`)}
                >
                  {t('cash.collect')}
                </button>
                {/* "Ekstre" §3.13.2: tek müşteriye özel döküm ucu O2 ile AÇILDI
                    (`GET /api/customers/:id/statement.pdf`). Düğme dönem penceresini açar;
                    PDF'i sunucu üretir, panelde para hesabı YOKTUR (kural 4). Şoför rolüne
                    düğme hiç gösterilmez — yetki sunucuda da zaten 403. */}
                {canStatement ? (
                  <button
                    className="btn"
                    style={{ fontSize: 12.5, padding: '7px 12px' }}
                    onClick={() => setStatementFor({ id: c.id, name: c.fullName })}
                  >
                    {t('cash.statement')}
                  </button>
                ) : null}
              </span>
            </div>
          );
        })}
      </div>
      {statementFor ? (
        <StatementDialog
          customerId={statementFor.id}
          customerName={statementFor.name}
          onClose={() => setStatementFor(null)}
        />
      ) : null}

      {/*
        "Aranacaklar" listesi — patronun patron sorusunu yanıtlar (O3 §1). Sunucu sıralı
        (oldestDays azalan, eşitse bakiye azalan); panel yalnız ilk 20'yi gösterip "Tümü"
        ile genişletir. Tahsilat butonu AccountsTab'taki mevcut `?customer=&collect=1`
        deseniyle aynı rota — Cash tahsilat ucuna yönlendirir.
      */}
      {callList.length > 0 ? (
        <div className="card" style={{ overflow: 'auto' }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'baseline',
              padding: '14px 22px 8px',
            }}
          >
            <span style={{ fontWeight: 700, fontSize: 14 }}>{t('aging.callList')}</span>
            {callList.length > 20 ? (
              <button
                className="btn"
                style={{ fontSize: 12, padding: '6px 11px' }}
                onClick={() => setShowAllCallList((v) => !v)}
              >
                {showAllCallList ? t('aging.collapse') : t('aging.showAll')}
              </button>
            ) : null}
          </div>
          <div
            className="table-head table-head-stack"
            style={{
              display: 'grid',
              gridTemplateColumns: CALL_LIST_GRID,
              minWidth: 940,
              padding: '0 22px',
              height: 44,
              alignItems: 'center',
              gap: 12,
            }}
          >
            <span>{t('cash.colCustomer')}</span>
            <span>{t('panel.phone')}</span>
            <span style={{ textAlign: 'right' }}>{t('panel.balance')}</span>
            <span>{t('aging.colOldestDebt')}</span>
            <span>{t('aging.lastPaymentAt')}</span>
            <span />
          </div>
          {callListTop20.map((c) => {
            const isOverdue = c.oldestDays !== null && c.oldestDays > 90;
            // `oldestUnpaidAt` bir GÜN string'i ("2026-07-01"), damga değil: `formatDateTime`
            // sahte saat basardı. Son tahsilat ise tam damga — `formatDateTime` doğru.
            const oldestLabel =
              c.oldestDays !== null && c.oldestUnpaidAt
                ? `${formatDate(c.oldestUnpaidAt)} · ${t('aging.oldestDays', { n: c.oldestDays })}`
                : '—';
            const lastCollected = c.lastPaymentAt
              ? formatDateTime(c.lastPaymentAt)
              : t('aging.neverCollected');
            return (
              <div
                key={c.customerId}
                className="rowgrid-stack"
                style={{
                  display: 'grid',
                  gridTemplateColumns: CALL_LIST_GRID,
                  minWidth: 940,
                  padding: '0 22px',
                  minHeight: 58,
                  alignItems: 'center',
                  gap: 12,
                  borderTop: '1px solid var(--border)',
                  background: isOverdue ? 'var(--bg-warning-float)' : undefined,
                }}
              >
                <span style={{ fontWeight: 500 }}>
                  {/* Anonimleştirilmiş müşterinin adı DB'de AYNEN durur: `retention.service.ts`
                      anonimleştirirken `fullName` alanına `kvkk.anonymizedName` yazdı. Panelde
                      ayrıca "(Anonimleştirilmiş müşteri)" eki YOK — aynı metni iki kez basardı. */}
                  {c.name}
                </span>
                <span style={{ fontSize: 13.5, color: 'var(--text-secondary)' }}>
                  {c.phone ? (
                    <a href={`tel:${c.phone}`} style={{ color: 'inherit' }}>
                      {c.phone}
                    </a>
                  ) : (
                    '—'
                  )}
                </span>
                <span style={{ textAlign: 'right', fontWeight: 600 }}>
                  {formatBalance(c.balance)}
                </span>
                <span
                  style={{
                    fontSize: 13,
                    color: isOverdue ? 'var(--danger)' : 'var(--text-secondary)',
                    fontWeight: isOverdue ? 600 : 400,
                  }}
                >
                  {oldestLabel}
                </span>
                <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
                  {lastCollected}
                </span>
                <span style={{ display: 'flex', gap: 8 }}>
                  {isOverdue ? (
                    <span
                      title={t('aging.overdue90')}
                      style={{
                        fontSize: 11,
                        padding: '4px 8px',
                        borderRadius: 12,
                        background: 'var(--danger-bg)',
                        color: 'var(--danger)',
                        fontWeight: 700,
                        letterSpacing: '.04em',
                        textTransform: 'uppercase',
                      }}
                    >
                      90+
                    </span>
                  ) : null}
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 12.5, padding: '7px 12px' }}
                    onClick={() => void navigate(`/musteriler?customer=${c.customerId}&collect=1`)}
                  >
                    {t('cash.collect')}
                  </button>
                </span>
              </div>
            );
          })}
        </div>
      ) : null}
    </>
  );
}

/**
 * Tek yaşlandırma bandı — sunucudan dört tutardan birini gösterir (panel toplamaz).
 * 90+ bandı sıfırdan büyükse tehlike rengine boyanır: patronun gözünden kaçmasın diye
 * görsel uyarı, sessiz metin değil (Faz 8 denetimi B-10).
 */
function AgingBand({
  label,
  value,
  danger,
}: {
  label: string;
  value: string | null;
  danger: boolean;
}) {
  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
        padding: '10px 12px',
        borderRadius: 'var(--r-pill)',
        background: danger ? 'var(--bg-warning-float)' : 'var(--surface)',
        border: danger ? '1px solid var(--danger)' : '1px solid var(--border)',
      }}
    >
      <span
        style={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: '.04em',
          textTransform: 'uppercase',
          color: danger ? 'var(--danger)' : 'var(--text-secondary)',
        }}
      >
        {label}
      </span>
      <span
        style={{
          fontSize: 15,
          fontWeight: 700,
          fontVariantNumeric: 'tabular-nums',
          color: danger ? 'var(--danger)' : 'var(--text-primary)',
        }}
      >
        {/* Sunucudan veri gelene kadar "—": yükleniyor ya da hata aldıysak ₺0,00 YAZMAK
            "borç yok" izlenimi verirdi (bilinmeyen ile sıfır aynı şey değildir). */}
        {value === null ? '—' : formatMoney(value)}
      </span>
    </div>
  );
}

function FloatsTab() {
  const rows = useDriverFloats();
  const summary = useFloatSummary();
  const { confirmHandover } = useFinanceMutations();
  const items = rows.data ?? [];

  return (
    <>
      <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {t('cash.floatsNote')}
      </p>
      <section className="kpi-grid" style={{ '--kpi-cols': 3 } as React.CSSProperties}>
        {/* Toplamlar SUNUCUDAN gelir: panelde `Number` ile toplamak hem kuruş yuvarlıyor hem de
            kesilmiş listeden eksik toplam üretiyordu (kural 4, Faz 3 denetimi K3). */}
        <Kpi
          label={t('cash.floatOnHand')}
          value={formatMoney(summary.data?.awaiting ?? '0')}
          accent={(summary.data?.awaitingCount ?? 0) > 0 ? 'warning' : undefined}
          note={t('cash.floatOnHandHint')}
        />
        <Kpi
          label={t('cash.floatSettled')}
          value={formatMoney(summary.data?.settledToday ?? '0')}
          note={t('cash.floatSettledHint')}
        />
        <Kpi label={t('cash.floatOnRoute')} value={formatMoney(summary.data?.onRoute ?? '0')} />
      </section>

      {confirmHandover.isError ? (
        <p style={{ fontSize: 13, color: 'var(--danger)' }}>
          {(confirmHandover.error as { message?: string } | null)?.message ?? t('error.internal')}
        </p>
      ) : null}

      <div className="card" style={{ overflow: 'auto' }}>
        <div
          className="table-head table-head-stack"
          style={{
            display: 'grid',
            gridTemplateColumns: '140px 1fr 140px 200px 180px',
            minWidth: 820,
            padding: '0 22px',
            height: 44,
            alignItems: 'center',
            gap: 12,
          }}
        >
          <span>{t('cash.colVehicle')}</span>
          <span>{t('cash.colDriver')}</span>
          <span style={{ textAlign: 'right' }}>{t('cash.colCash')}</span>
          <span>{t('cash.colStatus')}</span>
          <span />
        </div>
        {items.length === 0 ? (
          <p style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            {t('cash.noFloats')}
          </p>
        ) : (
          items.map((r) => (
            <div
              key={r.tripId}
              className="rowgrid-stack"
              style={{
                display: 'grid',
                gridTemplateColumns: '140px 1fr 140px 200px 180px',
                minWidth: 820,
                padding: '0 22px',
                height: 60,
                alignItems: 'center',
                gap: 12,
                borderTop: '1px solid var(--border)',
                background:
                  r.status === 'AWAITING_HANDOVER' ? 'var(--bg-warning-float)' : undefined,
              }}
            >
              <span style={{ fontWeight: 600, fontSize: 13.5 }}>{r.vehicle.plate}</span>
              <span>{r.driver.fullName}</span>
              <span style={{ textAlign: 'right', fontWeight: 700, fontSize: 15 }}>
                {formatMoney(r.status === 'ON_ROUTE' ? r.onHand : (r.settledAmount ?? r.declared))}
              </span>
              <span style={{ display: 'flex', alignItems: 'center', gap: 7, fontSize: 13.5 }}>
                <span
                  style={{
                    width: 9,
                    height: 9,
                    borderRadius: '50%',
                    background:
                      r.status === 'SETTLED'
                        ? 'var(--success)'
                        : r.status === 'ON_ROUTE'
                          ? 'var(--warning)'
                          : 'var(--primary)',
                  }}
                />
                {t(`floatStatus.${r.status}` as Parameters<typeof t>[0])}
              </span>
              <span>
                {r.status === 'AWAITING_HANDOVER' ? (
                  <button
                    className="btn btn-primary"
                    style={{ fontSize: 13, padding: '8px 14px' }}
                    disabled={confirmHandover.isPending}
                    onClick={() => confirmHandover.mutate(r.tripId)}
                  >
                    {t('cash.confirmHandover')}
                  </button>
                ) : null}
              </span>
            </div>
          ))
        )}
      </div>
    </>
  );
}

function ReceiptsTab() {
  const receipts = useReceipts();
  const items = receipts.data?.items ?? [];
  const [openReceiptId, setOpenReceiptId] = useState<string | null>(null);
  return (
    <>
      <p style={{ fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
        {t('cash.receiptsNote')}
      </p>
      {openReceiptId ? (
        <ReceiptModal receiptId={openReceiptId} onClose={() => setOpenReceiptId(null)} />
      ) : null}
      <div className="card" style={{ overflow: 'auto' }}>
        <div className="table-head receipts-grid" style={{ padding: '0 22px', height: 44 }}>
          <span>{t('cash.colReceiptNo')}</span>
          <span className="col-hide-sm">{t('cash.colTime')}</span>
          <span>{t('cash.colCustomer')}</span>
          <span className="col-hide-sm">{t('cash.colItems')}</span>
          <span style={{ textAlign: 'right' }}>{t('cash.colAmount')}</span>
          <span className="col-hide-sm">{t('cash.colPayment')}</span>
          <span />
        </div>
        {items.length === 0 ? (
          <p style={{ padding: '40px 24px', textAlign: 'center', color: 'var(--text-secondary)' }}>
            {t('cash.noReceipts')}
          </p>
        ) : (
          items.map((r) => (
            <div
              key={r.id}
              role="button"
              tabIndex={0}
              onClick={() => setOpenReceiptId(r.id)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') setOpenReceiptId(r.id);
              }}
              className="receipts-grid"
              style={{
                padding: '0 22px',
                height: 56,
                borderTop: '1px solid var(--border)',
                cursor: 'pointer',
              }}
            >
              <span style={{ fontFamily: 'var(--font-mono, monospace)', fontSize: 13 }}>
                MKB-{String(r.no).padStart(4, '0')}
              </span>
              <span className="col-hide-sm" style={{ fontSize: 13 }}>
                {formatDateTime(r.issuedAt).slice(-5)}
              </span>
              <span style={{ fontSize: 13.5 }}>{r.customer?.fullName ?? '—'}</span>
              <span
                className="col-hide-sm"
                style={{ fontSize: 13, color: 'var(--text-secondary)' }}
              >
                {r.order?.items.map((i) => `${i.qty}× ${i.product.name}`).join(' · ') ??
                  t(`receiptKind.${r.kind}` as Parameters<typeof t>[0])}
              </span>
              <span style={{ textAlign: 'right', fontWeight: 600 }}>{formatMoney(r.amount)}</span>
              <span className="col-hide-sm">
                <span
                  className={r.delivery?.paymentType === 'CREDIT' ? 'badge-amber' : 'badge-blue'}
                >
                  {r.delivery?.paymentType
                    ? t(`paymentType.${r.delivery.paymentType}` as Parameters<typeof t>[0])
                    : t(`receiptKind.${r.kind}` as Parameters<typeof t>[0])}
                </span>
              </span>
              <span onClick={(e) => e.stopPropagation()}>
                <PdfButton receiptId={r.id} />
              </span>
            </div>
          ))
        )}
      </div>
    </>
  );
}

/**
 * Makbuz PDF'ini açar.
 *
 * Düz bağlantı yerine `fetch` kullanılır: token bellekte durduğu ve sürüm başlıkları
 * gerektiği için `<a href>` sunucudan 400 alıyordu (Faz 3 denetimi U4).
 */
function PdfButton({ receiptId }: { receiptId: string }) {
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function open(): Promise<void> {
    setBusy(true);
    setFailed(false);
    try {
      const url = await api.getBlobUrl(`/api/receipts/${receiptId}/pdf`);
      window.open(url, '_blank', 'noreferrer');
      // Sekme açıldıktan sonra blob serbest bırakılır; erken bırakmak boş sekme üretir.
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch {
      setFailed(true);
    } finally {
      setBusy(false);
    }
  }
  return (
    <button
      onClick={() => {
        void open();
      }}
      disabled={busy}
      style={{ fontSize: 13, fontWeight: 600, color: failed ? 'var(--danger)' : 'var(--primary)' }}
    >
      <Icon name="list" size={14} /> {failed ? t('error.internal') : t('cash.viewPdf')}
    </button>
  );
}

function Dot({ color, label }: { color: string; label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
      <span style={{ width: 9, height: 9, borderRadius: '50%', background: color }} />
      <span className="section-label">{label}</span>
    </div>
  );
}

function Big({ value }: { value: string }) {
  return (
    <div className="kpi-number-sm" style={{ margin: '8px 0 12px' }}>
      {value}
    </div>
  );
}

function Line({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{ display: 'flex', justifyContent: 'space-between', padding: '5px 0', fontSize: 13.5 }}
    >
      <span style={{ color: 'var(--text-secondary)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  );
}
